import React from 'react';
import type { BillingAccount } from '../services/api';
import {
  BillingPlanContext,
  FeatureAccessKey,
  LockedFeatureInfo,
  LockedFeatureMap,
  getLockedFeature,
  getLockedTabs,
  hasFeatureAccess,
} from '../navigation/featureAccess';

interface FeatureAccessContextValue {
  account: BillingPlanContext | null;
  lockedTabs: LockedFeatureMap;
  hasFeature: (featureKey: FeatureAccessKey) => boolean;
  getLockedFeature: (featureKey: FeatureAccessKey) => LockedFeatureInfo | null;
}

const defaultValue: FeatureAccessContextValue = {
  account: null,
  lockedTabs: {},
  hasFeature: () => true,
  getLockedFeature: () => null,
};

const FeatureAccessContext = React.createContext<FeatureAccessContextValue>(defaultValue);

interface FeatureAccessProviderProps {
  account: BillingAccount | BillingPlanContext | null;
  children: React.ReactNode;
}

export const FeatureAccessProvider: React.FC<FeatureAccessProviderProps> = ({ account, children }) => {
  const value = React.useMemo<FeatureAccessContextValue>(() => ({
    account,
    lockedTabs: getLockedTabs(account),
    hasFeature: (featureKey) => hasFeatureAccess(featureKey, account),
    getLockedFeature: (featureKey) => getLockedFeature(featureKey, account),
  }), [account]);

  return (
    <FeatureAccessContext.Provider value={value}>
      {children}
    </FeatureAccessContext.Provider>
  );
};

const PLAYGROUND_FEATURES = new Set<FeatureAccessKey>([
  'workspace_mutation', 'prompt_run_execution', 'export_lifecycle',
]);

/** Scope Free access to the owned Playground subtree, never the surrounding index tools. */
export const PlaygroundFeatureAccessProvider: React.FC<{
  enabled?: boolean;
  children: React.ReactNode;
}> = ({ enabled = true, children }) => {
  const parent = React.useContext(FeatureAccessContext);
  const value = React.useMemo<FeatureAccessContextValue>(() => {
    if (!enabled || parent.account?.plan_id !== 'free') return parent;
    return {
      ...parent,
      hasFeature: (feature) => PLAYGROUND_FEATURES.has(feature) || parent.hasFeature(feature),
      getLockedFeature: (feature) => PLAYGROUND_FEATURES.has(feature) ? null : parent.getLockedFeature(feature),
    };
  }, [enabled, parent]);
  return <FeatureAccessContext.Provider value={value}>{children}</FeatureAccessContext.Provider>;
};

export const useFeatureAccess = (): FeatureAccessContextValue => React.useContext(FeatureAccessContext);
